// components/automations/AutomationModal.js
import React, { useState, useEffect } from 'react';
import { Modal, ModalFooter, Button, Input, Select, TextArea, ErrorMessage } from '../common';
import { Automation } from '../../models/Automation';
import { useTranslations } from '../../hooks/useTranslations';
import { useServicesPG } from '../../hooks/useServicesPG';
import { useTemplates } from '../../hooks/useTemplates';

export function AutomationModal({
  isOpen,
  onClose,
  onSubmit,
  automation = null,
  mode = 'add'
}) {
  const t = useTranslations();
  const { services, loading: servicesLoading } = useServicesPG();
  const { getTemplatesByTrigger, getDefaultTemplate, generatePreview, loading: templatesLoading } = useTemplates();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    triggerType: 'before_appointment',
    timing: 24,
    templateId: 'bq_appointment_reminder',
    isActive: true,
    serviceIds: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or automation changes
  useEffect(() => {
    if (isOpen) {
      if (automation && mode === 'edit') {
        setFormData({
          name: automation.name || '',
          description: automation.description || '',
          triggerType: automation.triggerType || 'before_appointment',
          timing: automation.timing || 48,
          templateId: automation.templateId || 'bq_appointment_reminder',
          isActive: automation.isActive !== undefined ? automation.isActive : true,
          serviceIds: automation.serviceIds || []
        });
      } else {
        // Reset form for new automation
        setFormData({
          name: '',
          description: '',
          triggerType: 'before_appointment',
          timing: 24,
          templateId: 'bq_appointment_reminder',
          isActive: false,
          serviceIds: []
        });
      }
      setErrors({});
    }
  }, [isOpen, automation, mode]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleTriggerTypeChange = (e) => {
    const newTriggerType = e.target.value;
    const defaultTemplate = getDefaultTemplate(newTriggerType);

    setFormData(prev => ({
      ...prev,
      triggerType: newTriggerType,
      timing: (newTriggerType === 'appointment_created' || newTriggerType === 'appointment_approved') ? 0 : (prev.timing || 48),
      templateId: defaultTemplate?.id || ''
    }));
  };

  // Handle "All Services" toggle
  const handleAllServicesToggle = (checked) => {
    if (checked) {
      // When "All Services" is checked, clear serviceIds array
      setFormData(prev => ({
        ...prev,
        serviceIds: []
      }));
    }
    // When unchecked, do nothing - let user select individual services
  };

  // Handle individual service toggle
  const handleServiceToggle = (serviceId, checked) => {
    setFormData(prev => {
      let newServiceIds;
      if (checked) {
        // Add service (this automatically makes it not "all services")
        newServiceIds = [...prev.serviceIds, serviceId];
      } else {
        // Remove service
        newServiceIds = prev.serviceIds.filter(id => id !== serviceId);
      }

      return {
        ...prev,
        serviceIds: newServiceIds
      };
    });
  };

  // Validate form
  const validateForm = () => {
    const validation = Automation.validate(formData);
    if (!validation.isValid) {
      const newErrors = {};
      validation.errors.forEach(error => {
        if (error.includes('name')) newErrors.name = error;
        else if (error.includes('trigger')) newErrors.triggerType = error;
        else if (error.includes('timing')) newErrors.timing = error;
        else if (error.includes('message')) newErrors.messageTemplate = error;
        else newErrors.general = error;
      });
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit(formData);
      if (result.success) {
        onClose();
      } else {
        setErrors({ general: result.error || t.failedToSaveAutomation });
      }
    } catch (error) {
      setErrors({ general: error.message || t.unexpectedErrorOccurred });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create translated trigger types
  const triggerTypes = [
    { value: 'before_appointment', label: t.beforeAppointmentReminder },
    { value: 'after_appointment', label: t.afterAppointmentFollowup },
    { value: 'appointment_created', label: 'לקוח הזמין תור' },
    { value: 'appointment_approved', label: t.whenAppointmentApproved }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <span>{mode === 'edit' ? t.editAutomation : t.createNewAutomation}</span>
        </div>
      }
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Error */}
        {errors.general && (
          <ErrorMessage message={errors.general} />
        )}

        {/* Automation Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t.automationName} *
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t.appointmentReminderExample}
            className={errors.name ? 'border-red-300' : ''}
            required
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            {t.description}
          </label>
          <Input
            id="description"
            name="description"
            type="text"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t.remindsClientsExample}
          />
        </div>

        {/* Trigger Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="triggerType" className="block text-sm font-medium text-gray-700 mb-1">
              {t.whenShouldThisRun} *
            </label>
            <Select
              id="triggerType"
              name="triggerType"
              value={formData.triggerType}
              onChange={handleTriggerTypeChange}
              options={triggerTypes}
              className={errors.triggerType ? 'border-red-300' : ''}
              required
            />
            {errors.triggerType && <p className="mt-1 text-sm text-red-600">{errors.triggerType}</p>}
          </div>

          {/* Timing (only show for before/after appointment) */}
          {formData.triggerType !== 'appointment_created' && formData.triggerType !== 'appointment_approved' && (
            <div>
              <label htmlFor="timing" className="block text-sm font-medium text-gray-700 mb-1">
                {formData.triggerType === 'before_appointment' ? t.hoursBeforeAppointment : t.hoursAfterAppointment}
              </label>
              <Input
                id="timing"
                name="timing"
                type="number"
                min="1"
                max="168"
                value={formData.timing}
                onChange={handleInputChange}
                className={errors.timing ? 'border-red-300' : ''}
                required
              />
              {errors.timing && <p className="mt-1 text-sm text-red-600">{errors.timing}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {t.between1And168Hours}
              </p>
            </div>
          )}

          {/* Template Selection */}
          <div className="md:col-span-2">
            <label htmlFor="templateId" className="block text-sm font-medium text-gray-700 mb-2">
              יש לבחור את תבנית הודעה *
            </label>
            <Select
              id="templateId"
              name="templateId"
              value={formData.templateId}
              onChange={handleInputChange}
              options={getTemplatesByTrigger(formData.triggerType).map(template => ({
                value: template.id,
                label: template.name
              }))}
              placeholder="בחר תבנית..."
              className={errors.templateId ? 'border-red-300' : ''}
              required
            />
            {errors.templateId && <p className="mt-1 text-sm text-red-600">{errors.templateId}</p>}
          </div>

          {/* Template Preview */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              תצוגה מקדימה של תבנית ההודעה
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              {formData.templateId && (
                <>
                  <div className="text-sm text-gray-600 mb-2 font-medium">
                    תבנית: {getTemplatesByTrigger(formData.triggerType).find(t => t.id === formData.templateId)?.name}
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {generatePreview(formData.templateId)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Services Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            הפעל אוטומציה על השירותים הנבחרים
          </label>

          {servicesLoading ? (
            <div className="text-sm text-gray-500">Loading services...</div>
          ) : (
            <div className="space-y-2">
              {/* All Services Option */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allServices"
                  checked={formData.serviceIds.length === 0}
                  onChange={(e) => handleAllServicesToggle(e.target.checked)}
                  className="h-4 w-4 ml-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allServices" className="text-sm font-medium text-gray-900">
                  כל השירותים
                </label>
              </div>

              {/* Individual Services */}
              {services.length > 0 && (
                <div className="pl-6 space-y-2 max-h-40 overflow-y-auto border-l-2 border-gray-200">
                  <div className="text-xs text-gray-500 mb-2">בחר שירותים ספציפיים:</div>
                  <div className="grid grid-cols-1 gap-2">
                    {services.map(service => (
                      <div key={service.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`service-${service.id}`}
                          checked={formData.serviceIds.includes(service.id)}
                          onChange={(e) => handleServiceToggle(service.id, e.target.checked)}
                          className="h-4 w-4 ml-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`service-${service.id}`} className="text-sm text-gray-700 truncate">
                          {service.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Help text */}
              <p className="text-xs text-gray-500 mt-2">
                {formData.serviceIds.length === 0
                  ? "האוטומציה תפעל על כל השירותים"
                  : `האוטומציה תפעל על ${formData.serviceIds.length} שירותים נבחרים`
                }
              </p>
            </div>
          )}
        </div>

        {/* Active Status */}
        <div className="flex items-center">
          <input
            id="isActive"
            name="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={handleInputChange}
            className="h-4 w-4 ml-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
            {t.activateThisAutomationImmediately}
          </label>
        </div>
      </form>

      <ModalFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t.cancel}
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{mode === 'edit' ? t.updating : t.creating}</span>
            </div>
          ) : (
            mode === 'edit' ? t.updateAutomation : t.createAutomation
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}